import { useState } from 'react'
import Icon from './Icon'
import { dayKey } from '../lib/format'

/**
 * Month grid, for finding a specific date quickly.
 *
 * The list is how you read a day; this is how you jump to one two weeks out
 * without scrolling past everything in between.
 */
export default function MonthCalendar({
  counts,
  showDays,
  selectedKey,
  onSelect,
  initialMonth,
}: {
  /** day key → number of itinerary items on it. */
  counts: Map<string, number>
  /** day keys that have a show, marked with an accent dot. */
  showDays: Set<string>
  selectedKey: string | null
  onSelect: (key: string) => void
  initialMonth: Date
}) {
  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(initialMonth))

  const cells = monthCells(visibleMonth)
  const todayKey = dayKey(new Date())

  return (
    <div className="panel panel-pad stack" style={{ gap: 12 }}>
      <div className="spread">
        <button
          className="btn btn-quiet"
          aria-label="Previous month"
          onClick={() => setVisibleMonth(shiftMonth(visibleMonth, -1))}
        >
          <Icon name="chevronLeft" size={15} />
        </button>
        <strong style={{ fontSize: '0.94rem' }}>
          {new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' }).format(
            visibleMonth,
          )}
        </strong>
        <button
          className="btn btn-quiet"
          aria-label="Next month"
          onClick={() => setVisibleMonth(shiftMonth(visibleMonth, 1))}
        >
          <Icon name="chevronRight" size={15} />
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((label, index) => (
          <span
            key={index}
            className="t-eyebrow"
            style={{ textAlign: 'center', paddingBottom: 4 }}
          >
            {label}
          </span>
        ))}

        {cells.map((date, index) => {
          if (!date) return <span key={`pad-${index}`} />

          const key = dayKey(date)
          const count = counts.get(key) ?? 0
          const hasShow = showDays.has(key)
          const selected = key === selectedKey
          const isToday = key === todayKey

          return (
            <button
              key={key}
              onClick={() => onSelect(key)}
              aria-pressed={selected}
              aria-label={`${date.getDate()}, ${count} item${count === 1 ? '' : 's'}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
                // 44px clears the minimum touch target on a laptop trackpad
                // and keeps the grid from feeling like a spreadsheet.
                height: 44,
                borderRadius: 8,
                background: selected ? 'var(--accent-wash)' : 'transparent',
                border: `1px solid ${selected ? 'var(--accent)' : 'transparent'}`,
                color: isToday ? 'var(--accent-text)' : 'var(--text-primary)',
                fontWeight: isToday || selected ? 600 : 400,
                fontSize: '0.82rem',
              }}
            >
              {date.getDate()}
              <span
                aria-hidden
                style={{
                  width: hasShow ? 5 : 4,
                  height: hasShow ? 5 : 4,
                  borderRadius: '50%',
                  background: hasShow
                    ? 'var(--accent)'
                    : count > 0
                      ? 'var(--text-tertiary)'
                      : 'transparent',
                }}
              />
            </button>
          )
        })}
      </div>
    </div>
  )
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function shiftMonth(date: Date, delta: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + delta, 1)
}

/** Days of the month padded so the 1st lands under the right weekday column. */
function monthCells(month: Date): Array<Date | null> {
  const first = startOfMonth(month)
  // Monday-first, matching the en-GB week the rest of the app formats in.
  const leading = (first.getDay() + 6) % 7
  const dayCount = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()

  const cells: Array<Date | null> = Array.from({ length: leading }, () => null)
  for (let day = 1; day <= dayCount; day += 1) {
    cells.push(new Date(month.getFullYear(), month.getMonth(), day))
  }
  return cells
}
